/**
 * Persistent prompt shown when an update has been downloaded and is ready to install.
 *
 * Features:
 * - Shows "Restart to update" button
 * - Dismissible (X) per downloaded version for the rest of the session — the
 *   update still installs on quit (`autoInstallOnAppQuit`), so closing the
 *   prompt only postpones it. A newer downloaded version shows it again.
 * - Displays target version
 * - Handles session save handshake before restart
 * - When the main process detected that previous restarts silently failed to
 *   install (installFailureDetected), offers a manual-download escape hatch —
 *   for these devices the Restart button alone demonstrably doesn't work.
 */

import { useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAutoUpdatesStore } from '../../../store/autoUpdatesStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { openExternalUrl } from '../../help-feedback';
import { UPDATE_DOWNLOAD_URL, macInstallIssueHint } from './UpdateFallbackBanner';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
/** Check if we're in a desktop (Electron) environment */
function isDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Electron') &&
    !!window.electronAPI?.autoUpdates
  );
}

export function UpdateReadyPrompt() {
  const { state } = useAutoUpdatesStore();
  const { track } = useAnalytics();
  const [isInstalling, setIsInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  const handleManualDownload = useCallback(() => {
    track('ui:update_fallback_download_clicked', { source: 'ready_prompt' });
    void openExternalUrl(UPDATE_DOWNLOAD_URL);
  }, [track]);

  const handleDismiss = useCallback((version: string) => {
    track('ui:update_ready_dismissed');
    setDismissedVersion(version);
  }, [track]);

  const handleRestartToUpdate = useCallback(async () => {
    if (!isDesktop()) return;

    setIsInstalling(true);
    setInstallError(null);

    try {
      // Call install - this triggers session save and quitAndInstall
      const result = await window.electronAPI!.autoUpdates.install();

      if (!result.success) {
        setInstallError(result.error || 'Failed to install update');
        setIsInstalling(false);
      }
      // If successful, app will restart - no need to reset state
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Install failed';
      setInstallError(message);
      setIsInstalling(false);
    }
  }, []);

  const handleRetry = useCallback(() => {
    setInstallError(null);
    handleRestartToUpdate();
  }, [handleRestartToUpdate]);

  // Don't render in web builds
  if (!isDesktop()) return null;

  // Only show when an update is downloaded
  const { status, updateInfo } = state;
  if (status !== 'downloaded' || !updateInfo) return null;
  if (updateInfo.version === dismissedVersion) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-border bg-background shadow-lg p-4"
      data-testid="update-ready-prompt"
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium block">
              Update ready to install
            </span>
            <span className="text-xs text-muted-foreground block truncate">
              Version {updateInfo.version}
            </span>
          </div>
          {!isInstalling && (
            <button
              type="button"
              onClick={() => handleDismiss(updateInfo.version)}
              className="rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 hover:bg-muted"
              aria-label="Dismiss"
              data-testid="dismiss-update-ready-prompt"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Error message */}
        {installError && (
          <div
            className="rounded-md bg-red-50 dark:bg-red-950 p-2 text-xs text-red-700 dark:text-red-300"
            data-testid="install-error-message"
          >
            {installError}
          </div>
        )}

        {/* Action button */}
        <div className="flex gap-2">
          {installError ? (
            <Button
              size="sm"
              className="flex-1"
              onClick={handleRetry}
              data-testid="retry-install-button"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Retry
            </Button>
          ) : (
            <Button
              size="sm"
              className="flex-1"
              onClick={handleRestartToUpdate}
              disabled={isInstalling}
              data-testid="restart-to-update-button"
            >
              {isInstalling ? (
                <>
                  <RandomDotmSpinner size={14} className="mr-1.5" />
                  Restarting...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  Restart to update
                </>
              )}
            </Button>
          )}
        </div>

        {/* Manual-download escape hatch for devices where restart demonstrably
            hasn't been installing the update (silent install failure). */}
        {state.installFailureDetected && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="update-ready-manual-download"
          >
            Restarting hasn&apos;t installed updates on this device.{' '}
            <button
              type="button"
              onClick={handleManualDownload}
              className="underline hover:text-foreground transition-colors"
              data-testid="update-ready-manual-download-link"
            >
              Download it manually
            </button>
            {macInstallIssueHint(state.macInstallIssue) && (
              <> {macInstallIssueHint(state.macInstallIssue)}</>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
